import {useEffect, useMemo, useState} from 'react';

import useEzRewardsEligibility from '@/hooks/useEzRewardsEligibility';
import {HELP_CENTER_API_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {suppressRewardsContent} from '@/utils/helpCenter/suppressRewardsContent';
import {type HelpCenterPayload} from '@/utils/helpCenter/types';

const EMPTY: HelpCenterPayload = {collections: [], articles: []};

export type HelpCenterFailure = {
  reason: 'network' | 'http' | 'parse';
  status?: number;
};

type UseHelpCenterResult = {
  data: HelpCenterPayload;
  loading: boolean;
  error: boolean;
  failure?: HelpCenterFailure;
};

const useHelpCenter = (): UseHelpCenterResult => {
  const [data, setData] = useState<HelpCenterPayload>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<HelpCenterFailure>();
  const {canAccessEzRewards} = useEzRewardsEligibility();

  useEffect(() => {
    let active = true;

    const load = async () => {
      const response = await fetch(compilePath(HELP_CENTER_API_PATH, {})).catch(() => null);

      if (!response) {
        if (active) setFailure({reason: 'network'});
        return;
      }

      if (!response.ok) {
        if (active) setFailure({reason: 'http', status: response.status});
        return;
      }

      const payload: HelpCenterPayload | null = await response.json().catch(() => null);

      if (!payload) {
        if (active) setFailure({reason: 'parse', status: response.status});
        return;
      }

      if (active) setData(payload);
    };

    load().finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(
    () => (canAccessEzRewards ? data : suppressRewardsContent(data)),
    [data, canAccessEzRewards],
  );

  return {data: filtered, loading, error: failure !== undefined, failure};
};

export default useHelpCenter;
